/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// ProgressLineChart configurations
import configs from "examples/Charts/LineCharts/ProgressLineChart/config";

const dashboardHeadingSx = {
  fontSize: "12px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const dashboardDataSx = {
  fontSize: "10px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

function ProgressLineChart({ color = "info", icon, title, count = 0, height = "100%", chart }) {
  const { data, options } = configs(color, chart.labels || [], title, chart.data || []);

  return (
   <Card sx={{ height: "100%", minHeight: 220 }}>
      <SoftBox display="flex" alignItems="center" px={2.5} pt={2.25} pb={1}>
        <SoftBox
          width="3.75rem"
          height="3.75rem"
          display="grid"
          justifyContent="center"
          alignItems="center"
          borderRadius="md"
          shadow="md"
          color="white"
          bgColor={color}
          variant="gradient"
        >
          <Icon fontSize="default">{icon}</Icon>
        </SoftBox>
        <SoftBox ml={2} lineHeight={1.25}>
          <SoftTypography
            variant="button"
            fontWeight="regular"
            textTransform="capitalize"
            color="text"
            sx={dashboardHeadingSx}
          >
            {title}
          </SoftTypography>
          {count ? (
            <SoftTypography
              component="div"
              variant="caption"
              fontWeight="regular"
              sx={dashboardDataSx}
            >
              {count}
            </SoftTypography>
          ) : null}
        </SoftBox>
      
      </SoftBox>
      {useMemo(
        () => (
          <SoftBox mt={1.5} px={1.5} pb={1.5}>
            <Line data={data} options={options} style={{ height}} />
          </SoftBox>
        ),
        [chart, height, color]
      )}
    </Card>
  );
}

// Typechecking props for the ProgressLineChart
ProgressLineChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  progress: PropTypes.number.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
};

export default ProgressLineChart;
